import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import React, { useEffect, useState, forwardRef, useCallback, useMemo, useRef, memo, useImperativeHandle, useContext } from 'react';
import { StyleSheet, View, Text, useWindowDimensions, Platform, Dimensions } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
// Removed debounce import - no longer using debouncing for maximum performance
import { Spinner } from 'tamagui';
import InfiniteGrid from '@components/ZoomableGridView/InfiniteGrid';
import ErrorBoundary from '@utils/ErrorBoundary';

// Generic interfaces for hierarchical items
export interface HierarchyItem {
  _id: string;
  [key: string]: any;
}

export interface HierarchyPosition {
  x: number;
  y: number;
}

export interface HierarchyLink {
  sourceId: string;
  targetId: string;
  type: string;
  color?: string;
  width?: number;
  sourceConnectionPoint?: 'left' | 'right' | 'top' | 'bottom';
  targetConnectionPoint?: 'left' | 'right' | 'top' | 'bottom';
  sourceConnector?: string; // Connector ID on source action (output)
  targetConnector?: string; // Connector ID on target action (input)
}

export interface BaseHierarchyHandle {
  setComponentPosition: (id: string, x: number, y: number) => void;
  getComponentPosition: (id: string) => { x: number, y: number } | null;
  centerAllComponents: () => void;
  moveTo: (x: number, y: number) => void;
  zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => void;
  zoomBy: (delta: number) => void;
  getCurrentZoom: () => number;
  getViewportSize: () => { width: number, height: number };
  forceUpdate: () => void;
  getAllItemPositions: () => Record<string, HierarchyPosition>;
  centerView: () => void;
  centerOnItem: (itemId: string) => void;
  centerOnPrimary: () => void;
}

export interface BaseHierarchyProps<T extends HierarchyItem> {
  items: T[];
  primaryItem?: T;
  links?: HierarchyLink[];
  isLoading?: boolean;
  onItemPositionChange?: (itemId: string, position: HierarchyPosition) => void;
  onSnap?: (target: any, source: any) => void;
  renderItem: (item: T, position: HierarchyPosition, onPositionChange: (position: HierarchyPosition) => void, zoomLevel?: number) => React.ReactNode;
  renderLinkHandler?: (props: {
    itemPositionsRef: React.RefObject<Record<string, HierarchyPosition>>;
    links: HierarchyLink[];
    theme: boolean;
    zoomLevel: number;
  }) => React.ReactNode;
  // Layout configuration
  viewportSize?: { width: number; height: number };
  gridSize?: number;
  initialZoom?: number;
  minZoom?: number;
  maxZoom?: number;
  initialViewportOffset?: { x: number; y: number }; // Override default centering
  // Positioning strategy
  positioningStrategy?: 'circular' | 'grid' | 'manual';
  centerSpacing?: number;
  horizontalOffset?: number; // Shift content left/right (0.0 = center, 0.3 = 30% left, -0.3 = 30% right)
  // Visual configuration
  showGrid?: boolean;
  showDebugInfo?: boolean;
  backgroundColor?: string;
}

// Constants - Reduced size for Android to prevent crashes
const DEFAULT_VIEWPORT_SIZE = Platform.OS === 'android'
  ? { width: 10000, height: 10000 }
  : { width: 20000, height: 20000 }; // Increased for larger flows
const DEFAULT_GRID_SIZE = 50;

// Main BaseHierarchy Component
const BaseHierarchy = forwardRef<BaseHierarchyHandle, BaseHierarchyProps<any>>((props, ref) => {
  const {
    items = [],
    primaryItem,
    links = [],
    isLoading = false,
    onItemPositionChange,
    onSnap,
    renderItem,
    renderLinkHandler,
    viewportSize = DEFAULT_VIEWPORT_SIZE,
    gridSize = DEFAULT_GRID_SIZE,
    initialZoom = 0.6,
    minZoom = 0.1,
    initialViewportOffset,
    maxZoom = 2,
    positioningStrategy = 'circular',
    centerSpacing = 400,
    horizontalOffset = 0, // Default to center (0.0)
    showGrid = false, // Temporarily disabled - focus on core functionality
    showDebugInfo = __DEV__,
    backgroundColor
  } = props;

  // Performance: Removed console.log for better performance

  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  const theme = themeSelector();

  // Get screen dimensions for proper centering
  const screenData = Dimensions.get('window');
  const screenWidth = screenData.width;
  const screenHeight = screenData.height;

  // Calculate proper centering offsets
  const viewportCenterX = viewportSize.width / 2;
  const viewportCenterY = viewportSize.height / 2;
  const screenCenterX = screenWidth / 2;
  const screenCenterY = screenHeight / 2;

  // Use custom initial offset if provided, otherwise center the viewport
  // Start with zero offsets - let moveTo handle all positioning
  const calculatedOffsetX = initialViewportOffset?.x ?? 0;
  const calculatedOffsetY = initialViewportOffset?.y ?? 0;

  // Refs for position and component management
  const itemPositionsRef = useRef<Record<string, HierarchyPosition>>({});
  const componentPositionsRef = useRef<Map<string, HierarchyPosition>>(new Map());
  const draggableComponentRefs = useRef<Map<string, any>>(new Map());
  const zoomableViewRef = useRef<ReactNativeZoomableView>(null);
  const [isZoomableViewReady, setIsZoomableViewReady] = useState(false);
  const linkHandlerRef = useRef<any>(null);
  const webContainerRef = useRef<View>(null);
  const lastZoomUpdateTime = useRef<number>(0);
  const zoomLevelRef = useRef<number>(initialZoom);

  // Track actual viewport dimensions from onLayout (like Context.tsx)
  const [viewportDimensions, setViewportDimensions] = useState<{ width: number; height: number } | null>(null);

  // Track pan offsets for Skia grid with throttled updates
  // Start at 0,0 - will be updated by onTransform
  const [gridOffsetX, setGridOffsetX] = useState(0);
  const [gridOffsetY, setGridOffsetY] = useState(0);
  const [gridZoomLevel, setGridZoomLevel] = useState(initialZoom);
  const lastGridUpdateTime = useRef<number>(0);
  const panOffsetX = useRef<number>(0);
  const panOffsetY = useRef<number>(0);

  // Web wheel handler with improved state synchronization
  const handleWheelZoom = useCallback((event: any) => {
    if (Platform.OS !== 'web') return;

    event.preventDefault();
    event.stopPropagation();

    const delta = event.deltaY;
    // Adjust sensitivity based on delta mode (pixel, line, or page)
    const sensitivityMultiplier = event.deltaMode === 1 ? 0.05 : 0.001; // Line mode vs pixel mode
    const zoomFactor = 1 - (delta * sensitivityMultiplier);

    const currentZoom = zoomLevel;
    const newZoom = Math.max(minZoom, Math.min(maxZoom, currentZoom * zoomFactor));

    if (Math.abs(newZoom - currentZoom) > 0.01 && zoomableViewRef.current) {
      // Calculate the zoom delta
      const zoomDelta = newZoom - currentZoom;
      
      // Update zoom level state immediately to prevent desync with gesture system
      setZoomLevel(newZoom);

      // Use zoomBy to adjust zoom
      try {
        zoomableViewRef.current.zoomBy(zoomDelta);
      } catch (error) {
        // Fallback: if zoomBy fails, the zoomLevel state is still updated
        // and will be corrected on next onTransform callback
        console.warn('Zoom by failed, state will sync on next transform:', error);
      }

      // Provide visual feedback
      if (showDebugInfo) {
        setRenderKey(prev => prev + 1);
      }
    }
  }, [zoomLevel, minZoom, maxZoom, showDebugInfo]);

  // Add wheel event listener for web
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let element: any = null;

    // For React Native Web, we need to find the actual DOM element
    const findDOMElement = () => {
      if (webContainerRef.current) {
        // Try to get the DOM node directly
        const container = webContainerRef.current as any;
        element = container?._nativeTag || container?.getNode?.() || container;

        // If we have a React Native element, try to get its DOM representation
        if (element && typeof element === 'object' && !element.addEventListener) {
          // This is likely a React Native component, look for DOM node
          element = element.findDOMNode?.() || element._reactInternals?.stateNode || null;
        }
      }
      return element;
    };

    // Add event listener with retry mechanism
    const addWheelListener = () => {
      element = findDOMElement();

      if (element && element.addEventListener) {
        // Adding wheel event listener for web
        element.addEventListener('wheel', handleWheelZoom, { passive: false });
        return true;
      }
      return false;
    };

    // Try to add listener immediately
    if (!addWheelListener()) {
      // If failed, try again after a short delay (component might not be fully mounted)
      const timer = setTimeout(() => {
        if (!addWheelListener()) {
          console.warn('🖱️ Could not attach wheel event listener - element not found');
        }
      }, 100);

      return () => {
        clearTimeout(timer);
        if (element && element.removeEventListener) {
          element.removeEventListener('wheel', handleWheelZoom);
        }
      };
    }

    return () => {
      // Removing wheel event listener
      if (element && element.removeEventListener) {
        element.removeEventListener('wheel', handleWheelZoom);
      }
    };
  }, [handleWheelZoom]);

  // All items including primary item
  const allItems = useMemo(() => {
    // Filter out null/undefined items and items without _id
    const validItems = items.filter(item => item != null && item._id != null);
    const itemsArray = [...validItems];

    if (primaryItem && primaryItem._id && !validItems.find(item => item._id === primaryItem._id)) {
      itemsArray.unshift(primaryItem);
    }

    // Performance: Removed console.log for better performance

    return itemsArray;
  }, [items, primaryItem]);

  // Position calculation based on strategy
  const calculatePositions = useCallback((strategy: string, itemsArray: any[]) => {
    const positions: Record<string, HierarchyPosition> = {};
    // Apply horizontal offset: 0.0 = center, positive = shift left, negative = shift right
    // e.g., 0.3 means shift 30% of viewport width to the left
    const centerX = viewportSize.width / 2 - (horizontalOffset * viewportSize.width);
    const centerY = viewportSize.height / 2;

    // Safety: Filter out invalid items
    const validItemsArray = itemsArray.filter(item => item != null && item._id != null);

    if (strategy === 'circular') {
      // Place primary item at center
      if (primaryItem && primaryItem._id) {
        positions[primaryItem._id] = { x: centerX, y: centerY };
      }

      // Position other items in a circular pattern
      const otherItems = validItemsArray.filter(item => item._id !== primaryItem?._id);
      otherItems.forEach((item, i) => {
        const angle = (i / Math.max(otherItems.length, 1)) * 2 * Math.PI;
        const radius = centerSpacing + (i % 2) * 200; // Vary radius for different rings
        positions[item._id] = {
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius
        };
      });
    } else if (strategy === 'grid') {
      // Grid layout
      const cols = Math.ceil(Math.sqrt(validItemsArray.length));
      const spacing = centerSpacing;

      validItemsArray.forEach((item, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const startX = centerX - ((cols - 1) * spacing) / 2;
        const startY = centerY - ((Math.ceil(validItemsArray.length / cols) - 1) * spacing) / 2;

        positions[item._id] = {
          x: startX + col * spacing,
          y: startY + row * spacing
        };
      });
    } else if (strategy === 'manual') {
      // Use existing positions from items
      validItemsArray.forEach(item => {
        if (item.x !== undefined && item.y !== undefined) {
          positions[item._id] = { x: item.x, y: item.y };
        }
      });
    }

    return positions;
  }, [viewportSize, primaryItem, centerSpacing, horizontalOffset]);

  // Initialize positions
  const initializePositions = useMemo(() => {
    // Performance: Removed console.log for better performance
    const result = calculatePositions(positioningStrategy, allItems);
    // Performance: Removed console.log for better performance
    return result;
  }, [calculatePositions, positioningStrategy, allItems]);

  // Initialize position refs IMMEDIATELY (not in useEffect to avoid timing issues)
  // This ensures positions are available for centering logic
  useMemo(() => {
    let positionsSet = 0;
    Object.entries(initializePositions).forEach(([itemId, position]) => {
      if (!itemPositionsRef.current[itemId]) {
        itemPositionsRef.current[itemId] = position;
        componentPositionsRef.current.set(itemId, position);
        positionsSet++;
      }
    });

    if (__DEV__ && positionsSet > 0) {
      console.log(`✅ Initialized ${positionsSet} positions:`, Object.keys(itemPositionsRef.current));
    }

    return positionsSet;
  }, [initializePositions]);

  // Position management functions
  const setComponentPosition = useCallback((id: string, x: number, y: number) => {
    const newPosition = { x, y };
    componentPositionsRef.current.set(id, newPosition);
    itemPositionsRef.current[id] = newPosition;

    if (linkHandlerRef.current?.forceUpdate) {
      linkHandlerRef.current.forceUpdate();
    }

    if (onItemPositionChange) {
      onItemPositionChange(id, newPosition);
    }
  }, [onItemPositionChange]);

  const getComponentPosition = useCallback((id: string): HierarchyPosition | null => {
    return componentPositionsRef.current.get(id) || itemPositionsRef.current[id] || null;
  }, []);

  const getAllItemPositions = useCallback(() => {
    return { ...itemPositionsRef.current };
  }, []);

  const centerAllComponents = useCallback(() => {
    // Performance: Removed console.log for better performance
    const newPositions = calculatePositions(positioningStrategy, allItems);

    // Update refs
    Object.entries(newPositions).forEach(([itemId, position]) => {
      itemPositionsRef.current[itemId] = position;
      componentPositionsRef.current.set(itemId, position);
    });
  }, [calculatePositions, positioningStrategy, allItems]);

  // Direct position update handler - no debouncing for maximum responsiveness
  const directPositionUpdate = useCallback((itemId: string, position: HierarchyPosition) => {
    if (!isComponentMounted) return;

    // Network communication or persistence would go here
    // Performance: Removed debouncing completely for smooth panning
  }, [isComponentMounted]);

  // Handle position changes from draggable components
  const handlePositionChange = useCallback((itemId: string, newPosition: HierarchyPosition) => {
    itemPositionsRef.current[itemId] = newPosition;
    componentPositionsRef.current.set(itemId, newPosition);

    if (linkHandlerRef.current?.forceUpdate) {
      linkHandlerRef.current.forceUpdate();
    }

    if (onItemPositionChange) {
      onItemPositionChange(itemId, newPosition);
    }
    directPositionUpdate(itemId, newPosition);
  }, [onItemPositionChange, directPositionUpdate]);

  // Component lifecycle
  useEffect(() => {
    setIsComponentMounted(true);
    // Performance: Removed console.log for better performance

    return () => {
      setIsComponentMounted(false);
    };
  }, []);

  // Separate effect to center on primary when both position and ref are ready
  const [hasAutoCentered, setHasAutoCentered] = useState(false);

  useEffect(() => {
    // Only run once
    if (hasAutoCentered || !primaryItem) {
      return;
    }

    // Wait for zoomable view to be ready
    if (!isZoomableViewReady) {
      if (__DEV__) {
        console.log('⏳ BaseHierarchy: Waiting for zoomable view to be ready...');
      }
      return;
    }

    // Wait for viewport dimensions to be measured
    if (!viewportDimensions) {
      if (__DEV__) {
        console.log('⏳ BaseHierarchy: Waiting for viewport dimensions...');
      }
      return;
    }

    // Check if position is ready
    const primaryPosition = itemPositionsRef.current[primaryItem._id];
    const totalPositions = Object.keys(itemPositionsRef.current).length;

    if (__DEV__) {
      console.log('🔍 BaseHierarchy: Checking primary centering readiness:', {
        primaryId: primaryItem._id,
        hasPrimaryPosition: !!primaryPosition,
        primaryPosition,
        isZoomableViewReady,
        hasZoomableRef: !!zoomableViewRef.current,
        hasAutoCentered,
        totalPositions,
        allPositionKeys: Object.keys(itemPositionsRef.current),
        viewportDimensions
      });
    }

    // Wait for positions to be initialized (need at least the primary position)
    if (!primaryPosition) {
      if (__DEV__) {
        console.log('⏳ BaseHierarchy: Waiting for primary position to be set...');
      }
      return;
    }

    if (primaryPosition && zoomableViewRef.current) {
      // Both are ready - center the view!
      const timer = setTimeout(() => {
        // Double-check they're still ready
        const pos = itemPositionsRef.current[primaryItem._id];
        if (pos && zoomableViewRef.current) {
          // moveTo(offsetX, offsetY) sets the pan offset directly
          // Formula: offset = (viewportCenter / zoom) - contentTarget
          // Use initialZoom since onTransform hasn't fired yet to update zoomLevelRef
          const currentZoom = initialZoom;
          // Use actual viewport dimensions from onLayout, fallback to screen dimensions
          const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
          const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

          // Center on the card's center, not its top-left
          // Assume standard card size of 320x400 (DraggableActionCard dimensions)
          const cardCenterX = pos.x + 160; // pos.x + cardWidth/2
          const cardCenterY = pos.y + 200; // pos.y + cardHeight/2

          const offsetX = (viewportCenterX / currentZoom) - cardCenterX;
          const offsetY = (viewportCenterY / currentZoom) - cardCenterY;


          try {
            // Call moveTo without animation flag (2 params only)
            (zoomableViewRef.current as any).moveTo(offsetX, offsetY);


            setHasAutoCentered(true);
          } catch (error) {
            console.error('❌ BaseHierarchy: moveTo failed:', error);
          }
        }
      }, 600); // Wait for zoomable view to fully initialize before centering

      return () => clearTimeout(timer);
    }
  }, [primaryItem, hasAutoCentered, isZoomableViewReady, viewportDimensions, initialZoom, screenWidth, screenHeight, Object.keys(itemPositionsRef.current).length]);

  // Additional centering effect after positions are calculated using correct formula
  // Only runs if there's NO primaryItem (otherwise the primary centering takes precedence)
  useEffect(() => {
    if (!primaryItem && Object.keys(initializePositions).length > 0 && zoomableViewRef.current) {
      const timer = setTimeout(() => {
        // Calculate the center of all items
        const positions = Object.values(initializePositions);
        if (positions.length > 0) {
          const contentCenterX = positions.reduce((sum, pos) => sum + pos.x, 0) / positions.length;
          const contentCenterY = positions.reduce((sum, pos) => sum + pos.y, 0) / positions.length;

          // moveTo(offsetX, offsetY) sets the pan offset directly
          // Formula: offset = (viewportCenter / zoom) - contentTarget
          const currentZoom = zoomLevelRef.current || initialZoom;
          const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
          const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

          const offsetX = (viewportCenterX / currentZoom) - contentCenterX;
          const offsetY = (viewportCenterY / currentZoom) - contentCenterY;

          if (__DEV__) {
            console.log('🎯 BaseHierarchy: Auto-centering on all items (no primary):', {
              contentCenter: { x: contentCenterX, y: contentCenterY },
              zoom: currentZoom,
              viewportDimensions,
              viewportCenter: { x: viewportCenterX, y: viewportCenterY },
              calculatedOffset: { x: offsetX, y: offsetY }
            });
          }

          (zoomableViewRef.current as any).moveTo(offsetX, offsetY, true);
        }
      }, 500); // Slightly longer delay to ensure all components are rendered

      return () => clearTimeout(timer);
    }
  }, [primaryItem, initializePositions, viewportDimensions, screenWidth, screenHeight, initialZoom]);

  // Expose imperative methods via ref
  useImperativeHandle(ref, () => ({
    setComponentPosition,
    getComponentPosition,
    getAllItemPositions,
    centerAllComponents,
    moveTo: (x: number, y: number) => zoomableViewRef.current?.moveTo(x, y),
    zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => zoomableViewRef.current?.zoomTo(zoom, zoomCenter),
    zoomBy: (delta: number) => zoomableViewRef.current?.zoomBy(delta),
    getCurrentZoom: () => zoomLevelRef.current, // Use ref for real-time accuracy
    getViewportSize: () => viewportSize,
    forceUpdate: () => setIsComponentMounted(prev => !prev),
    centerView: () => {
      if (zoomableViewRef.current) {
        const positions = Object.values(initializePositions);
        if (positions.length > 0) {
          const contentCenterX = positions.reduce((sum, pos) => sum + pos.x, 0) / positions.length;
          const contentCenterY = positions.reduce((sum, pos) => sum + pos.y, 0) / positions.length;

          // moveTo(offsetX, offsetY) sets the pan offset directly
          // Formula: offset = (viewportCenter / zoom) - contentTarget
          const currentZoom = zoomLevelRef.current || initialZoom;
          const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
          const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

          const offsetX = (viewportCenterX / currentZoom) - contentCenterX;
          const offsetY = (viewportCenterY / currentZoom) - contentCenterY;

          (zoomableViewRef.current as any).moveTo(offsetX, offsetY, true);
        }
      }
    },
    centerOnItem: (itemId: string) => {
      if (zoomableViewRef.current) {
        const itemPosition = itemPositionsRef.current[itemId];
        if (itemPosition) {
          // moveTo(offsetX, offsetY) sets the pan offset directly
          // Formula: offset = (viewportCenter / zoom) - contentTarget
          const currentZoom = zoomLevelRef.current || initialZoom;
          const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
          const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

          const offsetX = (viewportCenterX / currentZoom) - itemPosition.x;
          const offsetY = (viewportCenterY / currentZoom) - itemPosition.y;

          if (__DEV__) {
            console.log('🎯 BaseHierarchy centerOnItem:', {
              itemId,
              contentPosition: itemPosition,
              zoom: currentZoom,
              viewportDimensions,
              viewportCenter: { x: viewportCenterX, y: viewportCenterY },
              calculatedOffset: { x: offsetX, y: offsetY }
            });
          }

          (zoomableViewRef.current as any).moveTo(offsetX, offsetY, true);
        }
      }
    },
    centerOnPrimary: () => {
      if (zoomableViewRef.current && primaryItem) {
        const primaryPosition = itemPositionsRef.current[primaryItem._id];
        if (primaryPosition) {
          // moveTo(offsetX, offsetY) sets the pan offset directly
          // Formula: offset = (viewportCenter / zoom) - contentTarget
          const currentZoom = zoomLevelRef.current || initialZoom;
          const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
          const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

          const offsetX = (viewportCenterX / currentZoom) - primaryPosition.x;
          const offsetY = (viewportCenterY / currentZoom) - primaryPosition.y;

          if (__DEV__) {
            console.log('🎯 BaseHierarchy centerOnPrimary:', {
              primaryId: primaryItem._id,
              contentPosition: primaryPosition,
              zoom: currentZoom,
              viewportDimensions,
              viewportCenter: { x: viewportCenterX, y: viewportCenterY },
              calculatedOffset: { x: offsetX, y: offsetY }
            });
          }

          (zoomableViewRef.current as any).moveTo(offsetX, offsetY, true);
        }
      }
    }
  }), [setComponentPosition, getComponentPosition, getAllItemPositions, centerAllComponents, viewportSize, initializePositions, viewportDimensions, screenWidth, screenHeight, initialZoom, primaryItem]);

  // Don't render until positions are calculated
  if (Object.keys(initializePositions).length === 0 && allItems.length > 0) {
    // Performance: Removed console.log for better performance
    return null;
  }

  if (isLoading) {
    // Performance: Removed console.log for better performance
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Spinner />
        <Text style={{ marginTop: 10 }}>Loading hierarchy...</Text>
      </View>
    );
  }

  // Show message if no items to render
  if (allItems.length === 0) {
    // Performance: Removed console.log for better performance
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ fontSize: 18, color: theme ? '#666' : '#999', textAlign: 'center' }}>
          No items to display
        </Text>
        <Text style={{ fontSize: 14, color: theme ? '#888' : '#777', textAlign: 'center', marginTop: 8 }}>
          Add some items to see them here
        </Text>
      </View>
    );
  }

  // Performance: Removed console.log for better performance

  return (
    <View
      ref={webContainerRef}
      style={[
        { flex: 1 },
        Platform.OS === 'web' ? ({
          // Web-specific styles for wheel handling
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'none'
        } as any) : undefined
      ]}
      {...(Platform.OS === 'web' && {
        // Add web-specific props for better wheel handling
        onWheel: handleWheelZoom
      })}
    >
      <GestureHandlerRootView style={{ flex: 1 }}>
      <ReactNativeZoomableView
        ref={(ref) => {
          zoomableViewRef.current = ref;
          // Only set ready state once when ref first becomes available
            if (ref && !isZoomableViewReady) {
            setIsZoomableViewReady(true);
          }
        }}
        maxZoom={maxZoom}
        minZoom={minZoom}
          initialZoom={Platform.OS === 'android' ? Math.min(initialZoom, 0.5) : initialZoom}
        contentWidth={viewportSize.width}
        contentHeight={viewportSize.height}
        initialOffsetX={calculatedOffsetX}
        initialOffsetY={calculatedOffsetY}
          panBoundaryPadding={200}
        onLayout={(event) => {
          const { width, height } = event.nativeEvent.layout;
          setViewportDimensions({ width, height });
        }}
        onTransform={(event) => {
          try {
            // Update refs immediately for accurate readings
            zoomLevelRef.current = event.zoomLevel;
            panOffsetX.current = event.offsetX;
            panOffsetY.current = event.offsetY;

            // Throttle grid updates to 30fps (33ms) - Skia can handle this smoothly
            const now = Date.now();
            if (now - lastGridUpdateTime.current > 33) {
              lastGridUpdateTime.current = now;

              // InfiniteGrid wants to know what content coordinate is at viewport top-left
              // ReactNativeZoomableView's offset is the pan amount in content coordinates
              // Viewport top-left in content space = -offset / zoom
              // When zoomed out (zoom=0.5), offset=-1000 means content at 2000 is at viewport 0
              const visibleContentX = -event.offsetX / event.zoomLevel;
              const visibleContentY = -event.offsetY / event.zoomLevel;

              setGridZoomLevel(event.zoomLevel);
              setGridOffsetX(visibleContentX);
              setGridOffsetY(visibleContentY);
            }
          } catch (error) {
            // Silently catch any errors in transform callback to prevent rendering issues
            if (__DEV__) {
              console.warn('BaseHierarchy: Error in onTransform callback:', error);
            }
          }
        }}
        // Maximum performance optimizations for smooth panning
        doubleTapDelay={0}
        pinchToZoomInSensitivity={3}
        pinchToZoomOutSensitivity={3}
        movementSensibility={1.9}
        style={{ flex: 1 }}
      >
          <ErrorBoundary id="BaseHierarchy-Content" {...({
            children: (
              <View style={{
                width: viewportSize.width,
                height: viewportSize.height,
            backgroundColor: 'transparent',
            ...(Platform.OS !== 'web' && { pointerEvents: 'box-none' } as any)
          }}
            pointerEvents={Platform.OS !== 'web' ? 'box-none' : undefined}
          >


          {/* High-Performance Skia Grid - GPU accelerated with viewport culling */}
                {showGrid && (
            <View style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: viewportSize.width,
              height: viewportSize.height,
              pointerEvents: 'none',
              zIndex: 0,
              minWidth: 200,
              minHeight: 200
            }}>
              {__DEV__ && (
                <View style={{ position: 'absolute', top: 100, left: 10, backgroundColor: 'rgba(255,0,0,0.9)', padding: 8, borderRadius: 5, zIndex: 9999 }}>
                  <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>
                    🔴 GRID ENABLED z={gridZoomLevel.toFixed(2)}
                  </Text>
                  <Text style={{ color: 'white', fontSize: 10 }}>
                    offset: {gridOffsetX.toFixed(0)}, {gridOffsetY.toFixed(0)}
                  </Text>
                  <Text style={{ color: 'white', fontSize: 10 }}>
                    vp: {screenWidth}x{screenHeight}
                  </Text>
                </View>
              )}
              <InfiniteGrid
                zoomLevel={gridZoomLevel}
                offsetX={gridOffsetX}
                offsetY={gridOffsetY}
                viewportWidth={screenWidth}
                viewportHeight={screenHeight}
                cellSize={gridSize}
                color={theme ? '#000000' : '#FFFFFF'}
                opacity={0.05}
                lineWidth={2}
              />
            </View>
          )}

          {/* Link Handler */}
                {renderLinkHandler && (() => {
                  try {
                    const linkHandlerContent = renderLinkHandler({
                      itemPositionsRef,
                      links,
                      theme,
                      zoomLevel
                    });

                    // Ensure we have a valid React element (not a string, number, etc.)
                    if (linkHandlerContent === null || linkHandlerContent === undefined) {
                      return null;
                    } else if (typeof linkHandlerContent === 'string' || typeof linkHandlerContent === 'number') {
                      // If renderLinkHandler accidentally returns a string/number, wrap it in Text
                      return <Text>{String(linkHandlerContent)}</Text>;
                    } else {
                      return linkHandlerContent;
                    }
                  } catch (error) {
                    // If renderLinkHandler throws, show error in Text component
                    return (
                      <View style={{ padding: 10, backgroundColor: 'rgba(255,0,0,0.1)' }}>
                        <Text style={{ color: 'red', fontSize: 12 }}>
                          Error rendering links: {error instanceof Error ? error.message : String(error)}
                        </Text>
                      </View>
                    );
                  }
                })()}

          {/* Items */}
          <View style={{
            position: 'absolute',
            backgroundColor: 'transparent',
            overflow: 'visible',
                  zIndex: 1000,
            width: viewportSize.width,
              height: viewportSize.height,
              ...(Platform.OS !== 'web' && { pointerEvents: 'box-none' } as any)
            }}
              pointerEvents={Platform.OS !== 'web' ? 'box-none' : undefined}
            >
            {allItems.map((item) => {
              const initialPosition = itemPositionsRef.current[item._id] || initializePositions[item._id] || {
                x: viewportSize.width / 2,
                y: viewportSize.height / 2
              };

              // Performance: Removed console.log for better performance

              // Safely render the item - ensure it's always a valid React element
              let itemContent: React.ReactNode;
              try {
                const rendered = renderItem(item, initialPosition, (newPosition) =>
                  handlePositionChange(item._id, newPosition),
                  zoomLevel
                );

                // Ensure we have a valid React element (not a string, number, etc.)
                if (rendered === null || rendered === undefined) {
                  itemContent = null;
                } else if (typeof rendered === 'string' || typeof rendered === 'number') {
                  // If renderItem accidentally returns a string/number, wrap it in Text
                  itemContent = <Text>{String(rendered)}</Text>;
                } else {
                  itemContent = rendered;
                }
              } catch (error) {
                // If renderItem throws, show error in Text component
                itemContent = (
                  <View style={{ padding: 10, backgroundColor: 'rgba(255,0,0,0.1)' }}>
                    <Text style={{ color: 'red', fontSize: 12 }}>
                      Error rendering item: {error instanceof Error ? error.message : String(error)}
                    </Text>
                  </View>
                );
              }

              // Wrap in ErrorBoundary with proper children prop
              const errorBoundaryKey = `${item._id}-${renderKey}`;
              const errorBoundaryId = `BaseHierarchy-Item-${item._id}`;

              return (
                <ErrorBoundary key={errorBoundaryKey} id={errorBoundaryId} {...({
                  children: (
                    <View>
                      {itemContent}
                    </View>
                  )
                } as any)} />
              );
            })}

            {allItems.length === 0 && (
              <View style={{
                position: 'absolute',
                top: viewportSize.height / 2 - 50,
                left: viewportSize.width / 2 - 100,
                width: 200,
                height: 100,
                backgroundColor: 'rgba(255, 0, 0, 0.3)',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 10
              }}>
                <Text style={{ color: 'white', fontWeight: 'bold' }}>No Items to Display</Text>
                <Text style={{ color: 'white', fontSize: 12 }}>Check data sources</Text>
              </View>
            )}
          </View>


          {/* Zoom Level Indicator - REMOVED to prevent re-render issues */}
              </View>
            )
          } as any)} />
      </ReactNativeZoomableView>
      </GestureHandlerRootView>
    </View>
  );
});

BaseHierarchy.displayName = 'BaseHierarchy';

export default BaseHierarchy;
